import {
  type AroundATimeState,
  CalendarWithDateTimeFields,
  TimeRangeValue as FlexibleTimeRangeValue,
  type TimeRangeSelectionMode,
} from '@chronosphereio/chrono-ui';
import {
  AbsoluteTimeRange,
  formatWithTimeZone,
  isTimeRangeInputValueAbsolute,
  legacyTimeRangeToTimeRangeInputValue,
  TimeRangeInputValue,
  timeRangeInputValueToAbsolute,
  useFeatureFlags,
} from '@chronosphereio/core';
import { useMemo } from 'react';
import { DATE_TIME_FORMAT_12 } from '@/core';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { absoluteTimeRangeDatesHaveCorrectOrder } from '../utils';

const noop = () => {};
const DEFAULT_AROUND_A_TIME: AroundATimeState = {
  center: '',
  duration: '',
  setCenter: noop,
  setDuration: noop,
};

export interface TimeRangeTextFieldsProps {
  initialTimeRange: TimeRangeInputValue;
  onCancel: () => void;
  onTimeRangeChange: (timeRange: TimeRangeInputValue) => void;
  timeZone?: string;
  minDate?: Date;
  maxDate?: Date;
  aroundATime?: AroundATimeState;
  timeRangeSelectionMode?: TimeRangeSelectionMode;
  setTimeRangeSelectionMode?: (mode: TimeRangeSelectionMode) => void;
}

export const TimeRangeTextFields = ({
  initialTimeRange,
  onCancel,
  onTimeRangeChange,
  timeZone,
  minDate,
  maxDate,
  aroundATime = DEFAULT_AROUND_A_TIME,
  timeRangeSelectionMode,
  setTimeRangeSelectionMode,
}: TimeRangeTextFieldsProps) => {
  const timeFormat = useUserTimeFormat();
  const { enableNewDateTimePickerFormat } = useFeatureFlags();

  const baseAbsoluteRange = useMemo((): AbsoluteTimeRange => {
    return timeRangeInputValueToAbsolute(initialTimeRange);
  }, [initialTimeRange]);

  const timeRangeInTimezone = useMemo(() => {
    return {
      start: new Date(formatWithTimeZone(baseAbsoluteRange.start, DATE_TIME_FORMAT_12, timeZone)),
      end: new Date(formatWithTimeZone(baseAbsoluteRange.end, DATE_TIME_FORMAT_12, timeZone)),
    };
  }, [baseAbsoluteRange, timeZone]);

  const onApplyTimeRange = (timeRangeInputs: { start: Date | null; end: Date | null }) => {
    if (!timeRangeInputs.start || !timeRangeInputs.end) {
      return;
    }
    // Apply timezone adjustment
    const start = new Date(
      baseAbsoluteRange.start.getTime() + (timeRangeInputs.start.getTime() - timeRangeInTimezone.start.getTime())
    );
    const end = new Date(
      baseAbsoluteRange.end.getTime() + (timeRangeInputs.end.getTime() - timeRangeInTimezone.end.getTime())
    );
    onTimeRangeChange({ start, end });
  };

  const onApplyFlexibleTimeRange = (timeRange: FlexibleTimeRangeValue) => {
    const nextTimeRange = legacyTimeRangeToTimeRangeInputValue(timeRange);
    if (isTimeRangeInputValueAbsolute(nextTimeRange) && !absoluteTimeRangeDatesHaveCorrectOrder(nextTimeRange)) {
      // TODO: Currently we ignore selections with dates in the wrong order. Is that correct?
      return;
    }
    onTimeRangeChange(nextTimeRange);
  };

  return (
    <CalendarWithDateTimeFields
      {...(enableNewDateTimePickerFormat
        ? {
            inputMode: 'flexible',
            onApply: onApplyFlexibleTimeRange,
            initialTimeRange,
            aroundATime,
            timeRangeSelectionMode,
            setTimeRangeSelectionMode,
          }
        : {
            inputMode: 'structured',
            variant: 'range',
            onApply: onApplyTimeRange,
            combineDateTimeInput: enableNewDateTimePickerFormat,
            onCancel: onCancel,
            initialTimeRange: timeRangeInTimezone,
          })}
      timeZone={timeZone}
      minDate={minDate}
      maxDate={maxDate}
      timeFormat={timeFormat}
    />
  );
};
